import React from 'react';
import { getLockedStatusLabel, getStatusConfig, isStatusNotStarted, toLegacyStatusConfig } from '../../../FlowchartView/utils/statusUtils';

/**
 * StatusTrigger Component
 * 
 * The trigger button for the status dropdown.
 * Supports multiple display variants:
 * - 'pill': Compact rounded pill (TodoList view)
 * - 'button': Full button with label (Flow view)
 * - 'badge': Minimal badge style (Table view)
 */
const StatusTrigger = React.forwardRef(({
  variant = 'pill',
  currentStatus,
  statuses,
  isLocked,
  lockReason,
  hasNestedSprint,
  nestedSprintProgress,
  statusAutoSync = true,
  showLabel = false,
  onClick,
  onMouseDown,
  className = '',
}, ref) => {
  // Get current status config
  const currentStatusConfig = getStatusConfig(currentStatus, statuses);
  const statusConfig = toLegacyStatusConfig(currentStatusConfig);
  
  const isAutoSyncEnabled = hasNestedSprint && statusAutoSync !== false;
  const isManualOverride = hasNestedSprint && statusAutoSync === false;
  const showLockedLabel = isLocked && isStatusNotStarted(currentStatus, statuses);
  const isDisabled = showLockedLabel;

  // Determine display state
  let displayLabel, displayIcon, displayTitle;
  
  if (isAutoSyncEnabled) {
    // Show actual status name with auto-sync indicator
    displayLabel = statusConfig.label;
    displayIcon = '⚡';
    displayTitle = `${nestedSprintProgress || 0}% complete. Status auto-syncs with nested campaign. Click to override.`;
  } else if (isManualOverride) {
    displayLabel = statusConfig.label;
    displayIcon = null;
    displayTitle = 'Status manually set. Click to change or enable auto-sync.';
  } else if (showLockedLabel) {
    displayLabel = getLockedStatusLabel(lockReason);
    displayIcon = null;
    displayTitle = lockReason;
  } else {
    displayLabel = statusConfig.label;
    displayIcon = null;
    displayTitle = `Status: ${statusConfig.label}`;
  }

  const baseClass = 'status-dropdown__trigger';
  const variantClass = `${baseClass}--${variant}`;
  const stateClasses = [
    isDisabled && `${baseClass}--locked`,
    isAutoSyncEnabled && `${baseClass}--nested`,
    isManualOverride && `${baseClass}--manual-override`,
  ].filter(Boolean).join(' ');

  // Render based on variant
  if (variant === 'pill') {
    return (
      <button
        ref={ref}
        className={`${baseClass} ${variantClass} ${stateClasses} ${className}`}
        onClick={onClick}
        onMouseDown={(e) => {
          onMouseDown?.(e);
          e.stopPropagation();
        }}
        title={displayTitle}
        style={{
          '--status-color': currentStatusConfig.color,
          '--status-bg': `${currentStatusConfig.color}20`,
        }}
      >
        <span className="status-dropdown__trigger-dot" />
        <span className="status-dropdown__trigger-text">{displayLabel}</span>
        <svg 
          className="status-dropdown__trigger-chevron"
          width="10" 
          height="10" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <div className={`status-dropdown__row ${className}`}>
        {showLabel && (
          <span className="status-dropdown__field-label">Status</span>
        )}
        <button 
          ref={ref}
          className={`${baseClass} ${variantClass} ${stateClasses}`}
          onClick={onClick}
          onMouseDown={(e) => {
            onMouseDown?.(e);
            e.stopPropagation();
          }}
          title={displayTitle}
          style={{
            '--status-color': currentStatusConfig.color,
            '--status-bg': statusConfig.bg,
          }}
        >
          {displayIcon && <span className="status-dropdown__trigger-icon">{displayIcon}</span>}
          <span className="status-dropdown__trigger-label">{displayLabel}</span>
          {isManualOverride && (
            <span className="status-dropdown__override-indicator" title="Auto-sync disabled">⚡</span>
          )}
          <span className="status-dropdown__trigger-arrow">▾</span>
        </button>
      </div>
    );
  }

  // Badge variant (Table view style)
  return (
    <button
      ref={ref}
      className={`${baseClass} ${variantClass} ${stateClasses} ${className}`}
      onClick={onClick}
      onMouseDown={(e) => {
        onMouseDown?.(e);
        e.stopPropagation();
      }}
      title={displayTitle}
      style={{
        background: statusConfig.bg,
        color: statusConfig.color,
      }}
    >
      {displayLabel}
    </button>
  );
});

StatusTrigger.displayName = 'StatusTrigger';

export default StatusTrigger;
